import cn from 'classnames';
import { useEffect, useContext, useMemo } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useLocation } from 'react-router-dom';

import Text from 'components/common/Text';
import Loading from 'components/icons/Loading';
import {
  ProgressBarContext,
  UserContext,
  PickYourMealsContext,
} from 'components/signUpFunnel/Contexts';
import LegalCopy from 'components/signUpFunnel/LegalCopy';
import FunnelHeader from 'components/signUpFunnel/shared/FunnelHeader';
import { BrandContext } from 'context/BrandContext';
import { brandData } from 'helpers/BrandData';
import useAnalyticsCallback from 'hooks/useAnalyticsCallback';
import useAnalyticsEffect from 'hooks/useAnalyticsEffect';
import useBrand from 'hooks/useBrand';
import useFunnelMealPlan from 'hooks/useFunnelMealPlan';
import useMetadata from 'hooks/useMetadata';
import useScreenSizeConstants from 'hooks/useScreenSizeConstants';
import { TEMPO } from 'lib/constants';
import { createEvents } from 'services/RequestService/endpoints/signUp';
import styles from 'styles/components/signUpFunnel/thankYou/ThankYouContainer';

import InviteFriendsInstructions from './InviteFriendsInstructions';
import NextSteps from './NextSteps';
import PickYourMealsHero from './PickYourMealsHero';
import ShareInvitations from './ShareInvitations';

const ThankYouContainer = () => {
  const location = useLocation();
  const { setProgress } = useContext(ProgressBarContext);
  const { user, setUser } = useContext(UserContext);
  const { brand } = useContext(BrandContext);
  const { isPickYourMeals } = useContext(PickYourMealsContext);
  const { isMobile: isSmartphoneScreenSize, isMobileOrTablet } = useScreenSizeConstants();
  const mealPlanSlug = useFunnelMealPlan();
  const firstName = useMemo(() => user?.firstName, [user]);
  const isTempo = useBrand(TEMPO);

  const PickYourMealsGreeting = () => (
    <div className="flex flex-col items-center">
      <Text brandStylesOverrides="h1 text-tempo-black-100">Nicely done!</Text>
      <Text brandStylesOverrides="h1 text-tempo-black-100">Here’s what’s next:</Text>
    </div>
  );

  useMetadata({ title: `Step 6: Thank You | ${brandData(brand)?.displayName}` }, false);

  const onContinue = useAnalyticsCallback(
    null,
    ({ logEvent, events }) => {
      if (brand) {
        if (isPickYourMeals) {
          logEvent(events.pickMeals[TEMPO].thankYou.submitted, {
            mealPlanSlug: mealPlanSlug || TEMPO,
          });
        } else {
          logEvent(events.signUp[brand].thankYou.submitted, {
            mealPlanSlug: mealPlanSlug,
          });
        }
      }
    },
    [brand, mealPlanSlug, isPickYourMeals]
  );

  useAnalyticsEffect(
    ({ logEvent, events }) => {
      if (brand) {
        if (isPickYourMeals) {
          logEvent(events.pickMeals[TEMPO].thankYou.viewed, {
            mealPlanSlug: TEMPO,
          });
        } else {
          logEvent(events.signUp[brand].thankYou.viewed);
        }
      }
    },
    [brand, mealPlanSlug, isPickYourMeals]
  );

  useEffect(() => {
    setProgress({ percent: '80' });
    createEvents({ params: { event_name: 'PageView' } });
  }, []);

  useEffect(() => {
    if (location.state) setUser(user => ({ ...user, ...location.state.user }));
  }, [location.state]);

  const DefaultUserGreeting = () => {
    return (
      <FunnelHeader
        isCentered
        containerClassName={cn('px-4 pb-6 pt-12 bp1:px-0', {
          'bg-tempo-gray-100': isPickYourMeals,
        })}
        title={
          isPickYourMeals ? (
            <PickYourMealsGreeting />
          ) : firstName ? (
            `Welcome, ${firstName}!`
          ) : (
            <Skeleton count={2} className="px-4" />
          )
        }
      />
    );
  };

  const InvitationSection = () => {
    if (isMobileOrTablet) {
      return (
        <section>
          <ShareInvitations />
          <InviteFriendsInstructions isPickYourMeals={isPickYourMeals} />
        </section>
      );
    } else {
      return (
        <div className="mx-auto flex w-full max-w-5xl flex-row">
          <div className="w-1/2">
            <ShareInvitations />
          </div>
          <div className="w-1/2">
            <InviteFriendsInstructions isPickYourMeals={isPickYourMeals} />
          </div>
        </div>
      );
    }
  };

  const SmartphoneUserGreeting = () => {
    return (
      <FunnelHeader
        isCentered
        containerClassName="py-6"
        title={
          isPickYourMeals ? (
            <PickYourMealsGreeting />
          ) : firstName ? (
            `Welcome, ${firstName}!`
          ) : (
            "We're glad you've joined us!"
          )
        }
      />
    );
  };

  const SmartphoneInvitationSection = () => {
    return (
      <section className={cn(styles.smartphoneInvitationSection, 'relative flex flex-col')}>
        <ShareInvitations isSmartphoneScreenSize={isSmartphoneScreenSize} />
        <InviteFriendsInstructions
          isSmartphoneScreenSize={isSmartphoneScreenSize}
          isPickYourMeals={isPickYourMeals}
        />
      </section>
    );
  };

  const SmartphoneThankYouPage = () => {
    return (
      <section className={cn('px-6', isPickYourMeals ? 'bg-tempo-gray-40' : 'bg-transparent')}>
        {isPickYourMeals ? (
          <PickYourMealsHero onContinue={onContinue} />
        ) : (
          <>
            <SmartphoneUserGreeting />
            <NextSteps onClick={onContinue} isSmartphoneScreenSize={isSmartphoneScreenSize} />
          </>
        )}
        <SmartphoneInvitationSection />
        <LegalCopy onClick={onContinue} isSmartphoneScreenSize={isSmartphoneScreenSize} />
      </section>
    );
  };

  const DefaultThankYouPage = () => {
    return (
      <section className={cn({ 'bg-tempo-gray-40': isPickYourMeals })}>
        {isPickYourMeals ? (
          <PickYourMealsHero onContinue={onContinue} />
        ) : (
          <div className="flex items-center justify-center px-20">
            <div
              className={cn(
                'my-6 flex w-full max-w-5xl flex-col rounded-2xl border border-solid bp1:px-6',
                isTempo ? 'border-charcoal-80 bg-white-100' : 'border-cream-120 bg-cream-110'
              )}
            >
              <DefaultUserGreeting />
              <NextSteps onClick={onContinue} />
            </div>
          </div>
        )}
        <InvitationSection />
        <LegalCopy onClick={onContinue} />
      </section>
    );
  };

  return user ? (
    isSmartphoneScreenSize ? (
      <SmartphoneThankYouPage />
    ) : (
      <DefaultThankYouPage />
    )
  ) : (
    <Loading />
  );
};

export default ThankYouContainer;
